import { Controller, FieldValues } from 'react-hook-form';
import {
  Autocomplete,
  AutocompleteProps,
  PaperProps,
  styled,
  TextField,
  TextFieldProps,
} from '@mui/material';
import {
  autocompleteClasses,
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import arrowDown from 'assets/img/icons/redesign/arrow-down.svg';
import { IconClose } from 'assets/img';
import { filledInputClasses } from '@mui/material/FilledInput';
import { StyledIconWarning, StyledLabel } from 'redesign';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteChangeDetails } from '@mui/base';
import {
  FormMultiSelectProps,
  SelectAllExtended,
  SelectAllOption,
} from './form-multi-select.types';

const SELECT_ALL: SelectAllOption = { __selectAll: true } as const;

function isSelectAllOption(value: unknown): value is SelectAllOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    '__selectAll' in value &&
    value.__selectAll === true
  );
}

function getChangeValues<T>(
  values: Array<T | SelectAllOption> | T[],
  detail: AutocompleteChangeDetails<T> | undefined,
  options: T[],
  enableSelectAll: boolean,
  selectedValues: SelectAllExtended<T>[]
): T[] {
  const areAllValuesSelected = options.length > 0 && selectedValues.length === options.length;
  if (enableSelectAll && isSelectAllOption(detail?.option)) {
    if (areAllValuesSelected) {
      return [];
    } else {
      return options;
    }
  }
  return values as T[];
}

const LwFormMultiSelect = <
  T extends object = object,
  ControlFields extends FieldValues = FieldValues,
>({
  name,
  label,
  options,
  control,
  onChange,
  rules,
  disabled,
  'data-testid': dataTestId,
  ariaLabelInput,
  helperText,
  getOptionLabel,
  isOptionEqualToValue,
  renderOption,
  getOptionKey,
  isLoading,
  enableSelectAll = false,
  ...props
}: FormMultiSelectProps<T, ControlFields>) => {
  renderOption =
    renderOption ??
    ((props, option) => (
      <li {...props} key={getOptionKey(option)}>
        {getOptionLabel(option)}
      </li>
    ));

  const { t } = useTranslation('all-platforms-common');
  const selectAllLabel = t('dropdown.select_all', { ns: 'all-platforms-common' });
  const deselectAllLabel = t('dropdown.deselect_all', { ns: 'all-platforms-common' });

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const selectedValues: T[] = Array.isArray(field.value) ? field.value : [];
        const areAllValuesSelected = options.length > 0 && selectedValues.length === options.length;

        return (
          <StyledAutocomplete
            noOptionsText={'Geen resultaten gevonden'}
            {...field}
            {...props}
            value={selectedValues}
            title={label}
            aria-label={label}
            multiple
            loading={isLoading}
            loadingText="Laden..."
            options={[...(enableSelectAll ? [SELECT_ALL as T] : []), ...options]}
            getOptionLabel={(option) => {
              if (!isSelectAllOption(option)) {
                return getOptionLabel(option);
              }
              if (areAllValuesSelected) {
                return deselectAllLabel;
              }
              return selectAllLabel;
            }}
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement> & { key: any },
              option: SelectAllExtended<T>,
              state: AutocompleteRenderOptionState,
              ownerState: AutocompleteOwnerState<T, true, true, false, 'div'>
            ) => {
              if (isSelectAllOption(option)) {
                return (
                  <li {...props} key="__selectAll" data-testid="multi-select__select-all">
                    {areAllValuesSelected ? deselectAllLabel : selectAllLabel}
                  </li>
                );
              }
              return renderOption(props, option, state, ownerState);
            }}
            isOptionEqualToValue={(option, value) => {
              if (isSelectAllOption(option) || isSelectAllOption(value)) {
                return false;
              }

              if (isOptionEqualToValue) {
                return isOptionEqualToValue(option, value);
              }

              return option === value;
            }}
            filterSelectedOptions
            ChipProps={{ deleteIcon: <StyledCloseIcon data-testid="close-icon" /> }}
            disableClearable
            disabled={disabled}
            PaperComponent={(paperProps: PaperProps) => (
              <StyledAutocompletePaper hasError={!!error} {...paperProps} />
            )}
            data-testid={dataTestId}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label={
                  <StyledLabel
                    icon={error ? <StyledIconWarning /> : null}
                    required={!!rules?.required}
                    data-testid="multi-select-label"
                  >
                    {label}
                  </StyledLabel>
                }
                name={name}
                InputLabelProps={{
                  shrink: true,
                  htmlFor: name,
                }}
                InputProps={{
                  'aria-label': ariaLabelInput ?? label,
                  ...params.InputProps,
                }}
                error={!!error}
                helperText={error ? error.message : (helperText ?? null)}
              />
            )}
            onChange={(event, values, reason, detail) => {
              const changeValues = getChangeValues<T>(
                values,
                detail,
                options,
                enableSelectAll,
                selectedValues
              );
              field.onChange(changeValues);
              onChange?.(event, changeValues, reason, detail);
            }}
            aria-required={!!rules?.required}
          />
        );
      }}
    />
  );
};

export const StyledAutocompletePaper = styled('div')<{ hasError?: boolean }>(
  ({ theme, hasError }) => ({
    marginTop: theme.spacing(2),
    boxShadow: hasError
      ? '0px 12px 16px 0px rgba(204, 68, 0, 0.08)'
      : '0px 12px 16px 0px rgba(0, 26, 153, 0.08)',
    borderRadius: 12,
    backgroundColor: theme.palette.lwWhite[100],
    [`& .${autocompleteClasses.listbox}`]: {
      border: hasError
        ? `2px solid ${theme.palette.lwDanger[100]}`
        : `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
      borderRadius: 12,
      color: theme.palette.lwBlack[100],
      '::-webkit-scrollbar': {
        width: '20px',
      },
      '::-webkit-scrollbar-track': {
        background: theme.palette.lwPrimaryAccentDark[40],
        border: '8px solid white',
        borderRadius: theme.spacing(4),
      },

      '::-webkit-scrollbar-thumb': {
        background: theme.palette.lwPrimaryAccentDark[100],
        backgroundClip: 'content-box',
        border: '8px solid transparent',
        borderRadius: theme.spacing(4),
      },
      [`& .${autocompleteClasses.option}`]: {
        marginRight: 6,
        marginLeft: 6,
        alignItems: 'center',
        borderRadius: '8px',
        fontWeight: 600,
        fontFamily: 'Inter',
        fontSize: 14,
        '&:hover': {
          color: theme.palette.lwPrimaryAccentLight[100],
        },
        [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
          {
            backgroundColor: theme.palette.lwPrimaryAccentLight[5],
          },
      },
    },
  })
);

export type LwAutocompleteProps<T> = AutocompleteProps<T, true, true, false>;

export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  [`& .${autocompleteClasses.inputRoot}`]: {
    border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(3),
    marginTop: 0,
    '&:hover, &.Mui-focused': {
      borderColor: theme.palette.lwPrimaryAccentLight[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentDark[20]}`,
    },
    '&.Mui-error': {
      borderColor: theme.palette.lwDanger[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwDanger[20]}`,
    },
  },
  [`& .${autocompleteClasses.tag}`]: {
    backgroundColor: theme.palette.lwPrimaryAccentLight[10],
    fontWeight: 600,
    fontSize: '14px',
    fontFamily: 'Inter',
    lineHeight: '16px',
    height: '32px',
    gap: '8px',
    borderRadius: theme.spacing(6),
    padding: '6px 12px',
    margin: 0,
  },
  '.MuiChip-label': {
    color: theme.palette.lwPrimaryAccentLight[100],
    padding: 0,
  },
  [`& .${autocompleteClasses.popupIndicator}`]: {
    color: 'transparent',
    background: `url("${arrowDown}") no-repeat center center`,
    '&.Mui-disabled': {
      color: 'transparent',
      background: `url("${arrowDown}") no-repeat center center`,
    },
  },
  '.MuiAutocomplete-inputRoot:has(.MuiChip-root)': {
    paddingTop: 6,
    paddingBottom: 6,
  },
  [`& .${autocompleteClasses.endAdornment}`]: {
    marginRight: 10,
  },
  '&& .MuiChip-deleteIcon': {
    width: '16px',
    height: '16px',
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'center',
    margin: 0,
    fill: theme.palette.lwPrimaryAccentDark[100],
  },
  [`& .${formHelperTextClasses.root}`]: {
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: 'Inter',
  },
  [`& .${inputBaseClasses.root}`]: {
    display: 'inline-flex',
    alignContent: 'center',
    padding: '16px',
    gap: 8,
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  [`.${autocompleteClasses.input}`]: {
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: 'Inter',
  },
})) as typeof Autocomplete;

const StyledCloseIcon = styled(IconClose)(({ theme }) => ({
  fill: theme.palette.lwPrimaryAccentDark[60],
}));

const StyledTextField = styled(TextField)<TextFieldProps>(({ theme }) => ({
  [`& .${filledInputClasses.root}`]: {
    border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(3),
    '&:hover, &.Mui-focused': {
      borderColor: theme.palette.lwPrimaryAccentLight[100],
    },
    '&.Mui-error': {
      borderColor: theme.palette.lwDanger[100],
      boxShadow: `0 0 0 4px ${theme.palette.lwDanger[20]}`,
    },
  },
  [`& .${inputAdornmentClasses.root}`]: {
    marginRight: theme.spacing(20),
  },
}));

const LwMultiSelectUncontrolled = <
  T extends object = object,
  ControlFields extends FieldValues = FieldValues,
>({
  label,
  options,
  onChange,
  rules,
  disabled,
  'data-testid': dataTestId,
  ariaLabelInput,
  value,
  renderOption,
  getOptionKey,
  getOptionLabel,
}: Omit<FormMultiSelectProps<T, ControlFields>, 'control' | 'name'> & {
  value: any[];
}) => {
  return (
    <StyledAutocomplete
      value={value ?? []}
      title={label}
      multiple
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
      ChipProps={{ deleteIcon: <StyledCloseIcon /> }}
      filterSelectedOptions
      disableClearable
      onChange={onChange}
      disabled={disabled}
      PaperComponent={(paperProps: PaperProps) => <StyledAutocompletePaper {...paperProps} />}
      data-testid={dataTestId}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          label={label}
          InputLabelProps={{
            required: !!rules?.required,
            shrink: true,
          }}
          InputProps={{
            ...params.InputProps,
            'aria-label': ariaLabelInput,
          }}
        />
      )}
    />
  );
};

export { LwMultiSelectUncontrolled, LwFormMultiSelect };
